import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { KABAS, KABA_OPTIONS, FIT, PRICE } from "@/data/collection";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", colour: "", occasion_date: "", notes: "" };
const inputCls = "w-full border-0 border-b border-gold/30 bg-transparent px-0 py-3 text-base text-cream placeholder:text-stone2/70 outline-none transition-[border-color] duration-300 focus:border-gold";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2">{label}</span>
    {children}
  </label>
);

const Preview = ({ kaba }) => {
  const piece = KABAS.find((k) => k.id === kaba);
  return (
    <div className="relative hidden aspect-[3/4] w-full max-w-[360px] overflow-hidden rounded-t-full border border-gold/40 sm:block" data-testid="order-preview">
      <img key={kaba} src={piece?.image ?? "/images/real/closet_three.jpg"} alt={piece?.alt ?? "All three kabas hanging in a closet"}
        className="fade-in h-full w-full object-cover object-top" />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink to-transparent p-6 pt-16">
        <p className="font-display text-2xl text-cream">{piece?.name ?? "Bespoke"}</p>
      </div>
    </div>
  );
};

export const OrderForm = ({ kaba, setKaba }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const payload = { ...form, kaba };
    ["colour", "occasion_date", "notes"].forEach((k) => { if (!payload[k]) delete payload[k]; });
    try {
      const { data: order } = await axios.post(`${API}/orders`, payload);
      const { data: pay } = await axios.post(`${API}/payments/checkout`, { order_id: order.id, origin_url: window.location.origin });
      if (!pay?.checkout_url) throw new Error("no checkout url");
      window.location.href = pay.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn’t start your order", { description: Array.isArray(detail) ? "Please check your email and phone number." : "Please try again in a moment." });
      setSending(false);
    }
  };

  return (
    <section id="order" data-testid="order-section" className="mx-auto max-w-[1400px] px-5 py-28 sm:px-10 lg:py-40">
      <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-24">
        <Reveal>
          <Eyebrow>Order your kaba</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            Claim your <span className="italic text-white">crown</span> of cloth.
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-stone2 sm:text-lg">
            Choose a piece, add your details, then pay securely by card. Every cape is one size fits all, from XS to 2XL, cut to waist length — and one price, {PRICE}.
          </p>
          <div className="mt-12"><Preview kaba={kaba} /></div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="order-form" className="space-y-8 border border-gold/20 bg-ink2/80 p-6 backdrop-blur-xl sm:p-10">
            <Field label="Your kaba">
              <select value={kaba} onChange={(e) => setKaba(e.target.value)} data-testid="order-kaba-select" className={`${inputCls} cursor-pointer`}>
                {KABA_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-ink2">{o.name}</option>)}
              </select>
            </Field>
            <div className="grid gap-8 sm:grid-cols-2">
              <Field label="Full name"><input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="order-name-input" className={inputCls} /></Field>
              <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="order-email-input" className={inputCls} /></Field>
              <Field label="Phone / WhatsApp"><input required minLength={6} value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" data-testid="order-phone-input" className={inputCls} /></Field>
              <Field label="Occasion date (optional)"><input type="date" value={form.occasion_date} onChange={set("occasion_date")} data-testid="order-date-input" className={`${inputCls} [color-scheme:dark]`} /></Field>
            </div>
            <div data-testid="order-size-info" className="flex items-center gap-4 border-y border-gold/15 py-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2">Size</span>
              <span className="rounded-full border border-gold bg-gold/10 px-4 py-2 font-mono text-xs tracking-[0.15em] text-gold">{FIT}</span>
            </div>
            <div data-testid="order-price-info" className="flex items-baseline justify-between border-b border-gold/15 pb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2">Price</span>
              <span className="font-display text-3xl text-gold">{PRICE}</span>
            </div>
            <Field label="Colour preference (optional)"><input value={form.colour} onChange={set("colour")} placeholder="e.g. crimson or midnight blue" data-testid="order-colour-input" className={inputCls} /></Field>
            <Field label="Notes (optional)"><textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Occasion, styling questions, anything we should know" data-testid="order-notes-input" className={`${inputCls} resize-none`} /></Field>
            <button type="submit" disabled={sending} data-testid="order-submit-button"
              className="flex w-full items-center justify-center gap-3 rounded-full bg-gold py-5 font-mono text-xs uppercase tracking-[0.3em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-goldlight disabled:opacity-60">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {sending ? "Taking you to secure payment" : `Continue to payment · ${PRICE}`}
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-stone2">Secure card payment by Stripe · US shipping</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
